import React, { useMemo } from 'react';
import { DraftReviewTable, type DraftColumn } from '../../risks/components/DraftReviewTable.tsx';
import {
  controlDraftRowHasAnyMergedError,
  controlDraftRowHasSelectableBlockingError,
  mergedControlFieldReasons,
  type ControlBulkDraftRow,
  type ControlDraftTableField,
} from '../controlDraftValidation.ts';

export type { ControlBulkDraftRow, ControlDraftTableField as ControlBulkField };

export type ControlDraftReviewTableProps = {
  rows: ControlBulkDraftRow[];
  selectedIds: Set<string>;
  onToggleAll?: (checked: boolean) => void;
  onToggleRow: (rowId: string, checked: boolean) => void;
  selectAllAriaLabel?: string;
  rowSelectAriaLabel: (row: ControlBulkDraftRow) => string;
  getFieldValue: (row: ControlBulkDraftRow, field: ControlDraftTableField) => unknown;
  isFieldEditing: (rowId: string, field: string) => boolean;
  startEditing: (rowId: string, field: string) => void;
  stopEditing: () => void;
  updateField: (rowId: string, field: string, value: string) => void;
  showBackendValidationErrors: boolean;
  applyFrontendValidation?: boolean;
  initialPageSize?: number;
  pageSizeOptions?: number[];
};

const CONTROL_COLUMNS: DraftColumn<ControlDraftTableField>[] = [
  {
    type: 'text',
    field: 'clientControlId',
    header: 'Control ID',
    shellClassName: 'w-[10rem] shrink-0',
    multiline: true,
    emptyLabel: 'Click to add control id',
  },
  {
    type: 'text',
    field: 'title',
    header: 'Control Title',
    shellClassName: 'w-[14rem] shrink-0',
    multiline: true,
    emptyLabel: 'Click to add title',
  },
  {
    type: 'text',
    field: 'controlWordingText',
    header: 'Control Description',
    shellClassName: 'w-full min-w-[12rem]',
    multiline: true,
    emptyLabel: 'Click to add description',
  },
  {
    type: 'errors',
    field: 'otherFieldValidation',
    header: 'Validation Issues',
    shellClassName: 'w-[10rem] shrink-0',
  },
];

export function ControlDraftReviewTable({
  rows,
  selectedIds,
  onToggleAll,
  onToggleRow,
  selectAllAriaLabel,
  rowSelectAriaLabel,
  getFieldValue,
  isFieldEditing,
  startEditing,
  stopEditing,
  updateField,
  showBackendValidationErrors,
  applyFrontendValidation = true,
  initialPageSize = 10,
  pageSizeOptions,
}: ControlDraftReviewTableProps): React.ReactElement {
  const validationOpts = useMemo(
    () => ({
      showBackendErrors: showBackendValidationErrors,
      applyFrontendValidation,
    }),
    [showBackendValidationErrors, applyFrontendValidation]
  );

  return (
    <DraftReviewTable
      rows={rows}
      columns={CONTROL_COLUMNS}
      selectedIds={selectedIds}
      onToggleAll={onToggleAll}
      onToggleRow={onToggleRow}
      selectAllAriaLabel={selectAllAriaLabel}
      rowSelectAriaLabel={rowSelectAriaLabel}
      getFieldValue={getFieldValue}
      getFieldReasons={(row, field) => mergedControlFieldReasons(row, field, getFieldValue, validationOpts)}
      rowHasError={(row) => controlDraftRowHasAnyMergedError(row, getFieldValue, validationOpts)}
      rowBlocksSelection={(row) =>
        controlDraftRowHasSelectableBlockingError(row, getFieldValue, validationOpts)
      }
      isFieldEditing={isFieldEditing}
      startEditing={startEditing}
      stopEditing={stopEditing}
      updateField={updateField}
      initialPageSize={initialPageSize}
      pageSizeOptions={pageSizeOptions}
      compact
    />
  );
}

export default ControlDraftReviewTable;
